import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { resolveImageUrl } from "../utils/imageUrl";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Trophy, Clock, BookOpen, Users, Calendar, ChevronRight, Star,
  AlertCircle, CheckCircle, XCircle, Timer, Zap, Award, ArrowLeft
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function CountdownTimer({ targetDate }) {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      };
    };
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return (
    <div className="flex items-center justify-center gap-3">
      {[
        { val: timeLeft.days, label: "Days" },
        { val: timeLeft.hours, label: "Hours" },
        { val: timeLeft.minutes, label: "Min" },
        { val: timeLeft.seconds, label: "Sec" },
      ].map(({ val, label }) => (
        <div key={label} className="text-center">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-b from-blue-600 to-blue-700 flex items-center justify-center text-white text-xl font-bold shadow-md">
            {String(val || 0).padStart(2, "0")}
          </div>
          <p className="text-xs text-gray-500 mt-1">{label}</p>
        </div>
      ))}
    </div>
  );
}

// ============== STUDENT PROFILE FORM ==============
function StudentProfileForm({ onComplete }) {
  const { token } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", middle_name: "", school_name: "", school_location: "", class_level: "" });
  const [saving, setSaving] = useState(false);
  const [pictureFile, setPictureFile] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const fileInputRef = useRef(null);

  const handlePictureSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2MB"); return; }
    setPictureFile(file);
    setPicturePreview(URL.createObjectURL(file));
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.school_name.trim()) {
      toast.error("First name, last name, and school name are required");
      return;
    }
    setSaving(true);
    try {
      let profilePicUrl = null;
      // Upload picture first if selected
      if (pictureFile) {
        const fd = new FormData();
        fd.append("file", pictureFile);
        const picRes = await axios.post(`${API}/quiz-challenge/student-profile/upload-picture`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        profilePicUrl = picRes.data.picture_url;
      }
      await axios.post(`${API}/quiz-challenge/student-profile`, { ...form, profile_picture: profilePicUrl || form.profile_picture || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Profile saved!");
      onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving profile");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="student-profile-form">
      <Card className="rounded-2xl shadow-lg border-0">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-xl">Complete Your Profile</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Fill in your details to participate in Quiz Challenges and appear on the leaderboard</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Profile Picture Upload */}
          <div className="flex flex-col items-center gap-2">
            <div
              className="w-20 h-20 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-yellow-500 transition-colors overflow-hidden bg-gray-50"
              onClick={() => fileInputRef.current?.click()}
              data-testid="profile-picture-upload"
            >
              {picturePreview ? (
                <img src={picturePreview} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center">
                  <Award className="w-6 h-6 text-gray-400 mx-auto" />
                  <span className="text-[10px] text-gray-400">Photo</span>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePictureSelect} />
            <span className="text-xs text-gray-400">Profile picture (optional, max 2MB)</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">First Name *</label>
              <Input className="rounded-xl" placeholder="First name" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} data-testid="profile-first-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Last Name *</label>
              <Input className="rounded-xl" placeholder="Last name" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} data-testid="profile-last-name" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Middle Name (optional)</label>
            <Input className="rounded-xl" placeholder="Middle name" value={form.middle_name} onChange={e => setForm(f => ({ ...f, middle_name: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">School Name *</label>
            <Input className="rounded-xl" placeholder="Your school name" value={form.school_name} onChange={e => setForm(f => ({ ...f, school_name: e.target.value }))} data-testid="profile-school-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">School Location (optional)</label>
              <Input className="rounded-xl" placeholder="City / Region" value={form.school_location} onChange={e => setForm(f => ({ ...f, school_location: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Class Level (optional)</label>
              <Input className="rounded-xl" placeholder="e.g. Basic 9" value={form.class_level} onChange={e => setForm(f => ({ ...f, class_level: e.target.value }))} />
            </div>
          </div>
          <Button className="w-full rounded-xl gap-2 mt-2" onClick={submit} disabled={saving} data-testid="save-profile-btn">
            {saving ? "Saving..." : "Save & Continue"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ============== QUIZ TAKING COMPONENT ==============
function QuizTaker({ challengeId, subject, onComplete, token }) {
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await axios.get(`${API}/quiz-challenge/challenges/${challengeId}/subjects/${subject.id}/questions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setQuestions(res.data.questions);
        setTimeLeft(res.data.timer_minutes * 60);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Cannot load quiz");
        onComplete();
      }
    };
    fetchQuestions();
  }, [challengeId, subject.id, token]);

  const startQuiz = () => {
    setStarted(true);
    startTimeRef.current = new Date().toISOString();
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          submitQuiz(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const submitQuiz = async (autoSubmit = false) => {
    if (submitting) return;
    clearInterval(timerRef.current);
    setSubmitting(true);

    const totalTime = subject.timer_minutes * 60 - timeLeft;
    try {
      const res = await axios.post(`${API}/quiz-challenge/challenges/${challengeId}/subjects/${subject.id}/submit`, {
        answers,
        time_used_seconds: totalTime,
        started_at: startTimeRef.current,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
      if (autoSubmit) toast.info("Time's up! Quiz auto-submitted.");
      else toast.success("Quiz submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  useEffect(() => { return () => clearInterval(timerRef.current); }, []);

  if (result) {
    const pct = Math.round((result.score / result.total_questions) * 100);
    return (
      <div className="max-w-lg mx-auto text-center py-8" data-testid="quiz-result">
        <div className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${pct >= 70 ? "bg-green-100" : pct >= 40 ? "bg-yellow-100" : "bg-red-100"}`}>
          {pct >= 70 ? <CheckCircle className="w-12 h-12 text-green-600" /> : pct >= 40 ? <AlertCircle className="w-12 h-12 text-yellow-600" /> : <XCircle className="w-12 h-12 text-red-600" />}
        </div>
        <h2 className="text-2xl font-bold mb-2">{subject.subject_name} Complete!</h2>
        <p className="text-4xl font-black mb-1">{result.score}/{result.total_questions}</p>
        <p className="text-gray-500 mb-4">{pct}% — Time: {formatTime(result.time_used_seconds)}</p>
        <Button className="rounded-xl gap-2" onClick={onComplete} data-testid="back-to-challenge-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Challenge
        </Button>
      </div>
    );
  }

  if (!started) {
    return (
      <div className="max-w-lg mx-auto text-center py-8" data-testid="quiz-ready">
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
          <Zap className="w-10 h-10 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold mb-2">{subject.subject_name}</h2>
        <p className="text-gray-500 mb-1 capitalize">{subject.difficulty} difficulty</p>
        <p className="text-gray-500 mb-6">{questions.length} questions | {subject.timer_minutes} minutes</p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-sm text-yellow-800">
          <AlertCircle className="w-4 h-4 inline mr-1" />
          You can only attempt this quiz <strong>once</strong>. The timer starts when you click Begin.
        </div>
        <Button size="lg" className="rounded-xl gap-2 px-8" onClick={startQuiz} disabled={questions.length === 0} data-testid="begin-quiz-btn">
          <Timer className="w-5 h-5" /> Begin Quiz
        </Button>
      </div>
    );
  }

  const q = questions[currentQ];
  if (!q) return null;
  const timerPct = (timeLeft / (subject.timer_minutes * 60)) * 100;
  const isUrgent = timeLeft < 60;

  return (
    <div className="max-w-2xl mx-auto" data-testid="quiz-in-progress">
      {/* Timer bar */}
      <div className={`mb-6 rounded-xl overflow-hidden ${isUrgent ? "bg-red-100" : "bg-gray-100"}`}>
        <div className={`h-2 rounded-xl transition-all duration-1000 ${isUrgent ? "bg-red-500 animate-pulse" : "bg-blue-500"}`} style={{ width: `${timerPct}%` }} />
      </div>
      <div className="flex items-center justify-between mb-6">
        <span className="text-sm text-gray-500">Question {currentQ + 1} of {questions.length}</span>
        <span className={`font-mono font-bold text-lg ${isUrgent ? "text-red-600" : "text-gray-700"}`}>
          <Clock className="w-4 h-4 inline mr-1" />{formatTime(timeLeft)}
        </span>
      </div>

      {/* Question */}
      <Card className="rounded-2xl shadow-md border-0 mb-6">
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-6">{q.question_text}</h3>
          <div className="space-y-3">
            {["a", "b", "c", "d"].map(opt => (
              <button key={opt} data-testid={`option-${opt}`}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${answers[q.id] === opt ? "border-blue-500 bg-blue-50 text-blue-800 font-medium" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}
                onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}>
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full mr-3 text-sm font-bold ${answers[q.id] === opt ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"}`}>
                  {opt.toUpperCase()}
                </span>
                {q[`option_${opt}`]}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" className="rounded-xl" disabled={currentQ === 0} onClick={() => setCurrentQ(p => p - 1)}>Previous</Button>
        <span className="text-xs text-gray-400">{Object.keys(answers).length}/{questions.length} answered</span>
        {currentQ === questions.length - 1 ? (
          <Button className="rounded-xl gap-2 bg-green-600 hover:bg-green-700" onClick={() => submitQuiz()} disabled={submitting} data-testid="submit-quiz-btn">
            <CheckCircle className="w-4 h-4" /> {submitting ? "Submitting..." : "Submit Quiz"}
          </Button>
        ) : (
          <Button className="rounded-xl" onClick={() => setCurrentQ(p => p + 1)}>Next</Button>
        )}
      </div>
    </div>
  );
}

// ============== MAIN QUIZ CHALLENGE PAGE ==============
export default function QuizChallengePage() {
  const { user, token } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [upcomingChallenges, setUpcomingChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentProfile, setStudentProfile] = useState(null);
  const [profileChecked, setProfileChecked] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [takingQuiz, setTakingQuiz] = useState(null); // { challengeId, subject }
  const [myAttempts, setMyAttempts] = useState({});
  const [leaderboard, setLeaderboard] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchProfile = useCallback(async () => {
    if (!token) { setProfileChecked(true); return; }
    try {
      const res = await axios.get(`${API}/quiz-challenge/student-profile`, { headers });
      setStudentProfile(res.data.profile);
    } catch (err) { /* no profile */ }
    setProfileChecked(true);
  }, [token]);

  const fetchChallenges = useCallback(async () => {
    try {
      const [activeRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/quiz-challenge/challenges`),
        axios.get(`${API}/quiz-challenge/upcoming`),
      ]);
      setChallenges(activeRes.data.challenges);
      setUpcomingChallenges(upcomingRes.data.upcoming || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  const fetchMyAttempts = useCallback(async (challengeId) => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/quiz-challenge/my-attempts/${challengeId}`, { headers });
      setMyAttempts(prev => ({ ...prev, [challengeId]: res.data }));
    } catch (err) { /* no attempts */ }
  }, [token]);

  const fetchLeaderboard = async (challengeId) => {
    setLeaderboard(null);
    setShowLeaderboard(true);
    try {
      const res = await axios.get(`${API}/quiz-challenge/challenges/${challengeId}/leaderboard`);
      setLeaderboard(res.data);
    } catch (err) { toast.error("Failed to load leaderboard"); setShowLeaderboard(false); }
  };

  useEffect(() => { fetchProfile(); fetchChallenges(); }, [fetchProfile, fetchChallenges]);

  useEffect(() => {
    if (selectedChallenge && token) fetchMyAttempts(selectedChallenge.id);
  }, [selectedChallenge, token, fetchMyAttempts]);

  const isStudent = user?.user_type === "student" || user?.user_type === "pending";

  // If taking a quiz, show the quiz taker
  if (takingQuiz) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16 px-4">
        <QuizTaker
          challengeId={takingQuiz.challengeId}
          subject={takingQuiz.subject}
          token={token}
          onComplete={() => {
            setTakingQuiz(null);
            if (selectedChallenge) fetchMyAttempts(selectedChallenge.id);
          }}
        />
      </div>
    );
  }

  // If viewing a specific challenge
  if (selectedChallenge) {
    const attempts = myAttempts[selectedChallenge.id];
    const attemptedSubjects = new Set(attempts?.attempts?.map(a => a.subject_id) || []);

    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <button className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6" onClick={() => setSelectedChallenge(null)} data-testid="back-to-challenges">
            <ArrowLeft className="w-4 h-4" /> Back to Challenges
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedChallenge.title}</h1>
            <p className="text-gray-500">{selectedChallenge.description}</p>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500 flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> Season {selectedChallenge.season_number}</span>
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {selectedChallenge.season_duration_months} month season</span>
              {selectedChallenge.end_date && (
                <span className="flex items-center gap-1 text-orange-600">
                  <AlertCircle className="w-4 h-4" /> Ends {new Date(selectedChallenge.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              )}
              <Button variant="outline" size="sm" className="rounded-xl gap-1" onClick={() => fetchLeaderboard(selectedChallenge.id)} data-testid="view-leaderboard">
                <Trophy className="w-4 h-4 text-yellow-500" /> View Leaderboard
              </Button>
            </div>
          </div>

          {/* My Progress */}
          {attempts && attempts.attempts.length > 0 && (
            <Card className="rounded-2xl shadow-sm border-0 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardContent className="py-4">
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Your Progress</h3>
                <div className="flex items-center gap-8 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">
                      {attempts.attempts.reduce((sum, a) => sum + Math.round((a.score / a.total_questions) * 100), 0)}%
                    </p>
                    <p className="text-xs text-gray-500">Total Score</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{attempts.attempts.length}</p>
                    <p className="text-xs text-gray-500">Subjects Done</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{formatTime(attempts.total_time_seconds)}</p>
                    <p className="text-xs text-gray-500">Total Time</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Subjects Grid */}
          <h3 className="font-semibold text-lg text-gray-800 mb-4">Subjects</h3>
          {selectedChallenge.subjects?.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No subjects available yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedChallenge.subjects?.map(sub => {
                const done = attemptedSubjects.has(sub.id);
                const attempt = attempts?.attempts?.find(a => a.subject_id === sub.id);
                return (
                  <Card key={sub.id} className={`rounded-2xl shadow-sm border-0 transition-all hover:shadow-md ${done ? "opacity-80" : ""}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-800">{sub.subject_name}</h4>
                          {sub.description && <p className="text-xs text-gray-500 mt-0.5">{sub.description}</p>}
                        </div>
                        <Badge variant={sub.difficulty === "easy" ? "secondary" : sub.difficulty === "hard" ? "destructive" : "outline"} className="capitalize text-xs">
                          {sub.difficulty}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                        <span><Clock className="w-3.5 h-3.5 inline mr-1" />{sub.timer_minutes} min</span>
                        <span><BookOpen className="w-3.5 h-3.5 inline mr-1" />{sub.total_questions} questions</span>
                      </div>
                      {done ? (
                        <div className="bg-green-50 rounded-xl p-3 text-center">
                          <CheckCircle className="w-5 h-5 inline text-green-600 mr-1" />
                          <span className="text-sm font-medium text-green-700">
                            Completed — {attempt?.score}/{attempt?.total_questions} ({formatTime(attempt?.time_used_seconds || 0)})
                          </span>
                        </div>
                      ) : (
                        <Button className="w-full rounded-xl gap-2" onClick={() => {
                          if (!user) { toast.error("Please sign in to participate"); return; }
                          if (!studentProfile) { toast.error("Complete your profile first"); return; }
                          setTakingQuiz({ challengeId: selectedChallenge.id, subject: sub });
                        }} data-testid={`start-quiz-${sub.id}`}>
                          <Zap className="w-4 h-4" /> Start Quiz
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Leaderboard Dialog */}
        <Dialog open={showLeaderboard} onOpenChange={setShowLeaderboard}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle><Trophy className="w-5 h-5 inline mr-2 text-yellow-500" />Leaderboard — {selectedChallenge?.title}</DialogTitle>
              {leaderboard?.challenge && (
                <p className="text-xs text-gray-400 mt-1">
                  Season {leaderboard.challenge.season_number}
                  {leaderboard.challenge.start_date && ` | ${new Date(leaderboard.challenge.start_date).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}`}
                  {leaderboard.challenge.end_date && ` — ${new Date(leaderboard.challenge.end_date).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}`}
                </p>
              )}
            </DialogHeader>
            {!leaderboard ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : leaderboard.leaderboard.length === 0 ? (
                  <p className="text-center py-8 text-gray-400" style={{ animation: "fadeSlideIn 0.3s ease-out" }}>No participants yet. Be the first!</p>
                ) : (
              <div className="space-y-4" style={{ animation: "fadeSlideIn 0.3s ease-out" }}>
                {leaderboard.leaderboard.map((entry, i) => (
                  <div key={entry.user_id} style={{ animation: `fadeSlideIn 0.3s ease-out ${i * 0.05}s both` }}
                    className={`rounded-xl border overflow-hidden ${i === 0 ? "border-yellow-400 bg-yellow-50" : i === 1 ? "border-gray-300 bg-gray-50" : i === 2 ? "border-orange-400 bg-orange-50" : "border-gray-200 bg-white"}`}>
                    {/* Header row */}
                    <div className="flex items-center gap-3 p-4 pb-2">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${i === 0 ? "bg-yellow-500 text-white" : i === 1 ? "bg-gray-400 text-white" : i === 2 ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-600"}`}>
                        {i === 0 ? <Award className="w-5 h-5" /> : entry.rank}
                      </div>
                      {entry.profile_picture ? (
                        <img src={resolveImageUrl(entry.profile_picture)} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-500 text-sm font-bold shrink-0">
                          {entry.first_name?.[0]}{entry.last_name?.[0]}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-gray-800">{entry.first_name} {entry.middle_name || ""} {entry.last_name}</p>
                        <p className="text-xs text-gray-500">
                          {entry.school_name}{entry.school_location ? ` — ${entry.school_location}` : ""}
                          {entry.class_level ? ` | ${entry.class_level}` : ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-xl text-gray-900">{entry.total_percentage}<span className="text-sm text-gray-400">%</span></p>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wide">Total Score</p>
                      </div>
                    </div>
                    {/* Subject breakdown */}
                    <div className="px-4 pb-3 pt-1">
                      <div className="grid gap-1.5">
                        {entry.subject_scores.map(ss => (
                          <div key={ss.subject_id} className="flex items-center justify-between text-xs bg-white/60 rounded-lg px-3 py-1.5 border border-gray-100">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-700">{ss.subject_name}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize font-medium ${ss.difficulty === "easy" ? "bg-green-100 text-green-700" : ss.difficulty === "hard" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{ss.difficulty}</span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-600">
                              <span>{ss.percentage}%</span>
                              <span className="text-gray-400">{ss.score}/{ss.total_questions}</span>
                              <span className="text-gray-400">{formatTime(ss.time_used_seconds)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---- Main Challenge Listing ----
  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 px-4" data-testid="quiz-challenge-page">
      {/* Hero */}
      <div className="max-w-4xl mx-auto text-center mb-12">
        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
          <Trophy className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-3">Quiz Challenge</h1>
        <p className="text-gray-500 text-lg max-w-2xl mx-auto">
          Compete with students across Ghana! Take quizzes, climb the leaderboard, and prove your academic excellence.
        </p>
      </div>

      {/* Profile check for students */}
      {user && isStudent && profileChecked && !studentProfile && (
        <div className="mb-12">
          <StudentProfileForm onComplete={() => fetchProfile()} />
        </div>
      )}

      {!user && (
        <div className="max-w-lg mx-auto mb-12 bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-800 mb-1">Sign in to participate</h3>
          <p className="text-sm text-gray-500">Create a student account to take quizzes and appear on the leaderboard.</p>
        </div>
      )}

      {/* Challenges Grid */}
      <div className="max-w-4xl mx-auto">
        <h2 className="text-xl font-bold text-gray-800 mb-6">Available Challenges</h2>
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading challenges...</div>
        ) : challenges.length === 0 ? (
          <div className="text-center py-16">
            <Trophy className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400">No challenges available yet</h3>
            <p className="text-sm text-gray-400 mt-1">Check back soon for new quiz competitions!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {challenges.map(ch => {
              const isUpcoming = ch.status === "upcoming";
              return (
                <Card key={ch.id} className={`rounded-2xl shadow-sm border-0 hover:shadow-lg transition-all cursor-pointer ${isUpcoming ? "opacity-90" : ""}`} onClick={async () => {
                  if (isUpcoming) { toast.info("This challenge hasn't started yet. Check the start date!"); return; }
                  try {
                    const res = await axios.get(`${API}/quiz-challenge/challenges/${ch.id}`);
                    setSelectedChallenge(res.data);
                  } catch (err) { toast.error("Failed to load challenge"); }
                }} data-testid={`challenge-card-${ch.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <Badge variant={ch.status === "active" ? "default" : "secondary"} className={`capitalize ${ch.status === "active" ? "bg-green-500" : ""}`}>
                        {ch.status}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{ch.title}</h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">{ch.description}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span><BookOpen className="w-3.5 h-3.5 inline mr-1" />{ch.subject_count} subjects</span>
                      <span><Calendar className="w-3.5 h-3.5 inline mr-1" />Season {ch.season_number}</span>
                    </div>
                    {isUpcoming && ch.start_date && (
                      <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4">
                        <p className="text-xs font-semibold text-blue-700 text-center mb-3">New Quiz Challenge Coming Soon</p>
                        <CountdownTimer targetDate={ch.start_date} />
                        <p className="text-xs text-blue-500 text-center mt-2">
                          Starts {new Date(ch.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                        </p>
                      </div>
                    )}
                    {ch.status === "active" && (
                      <Button variant="outline" className="w-full mt-4 rounded-xl gap-2" data-testid={`enter-challenge-${ch.id}`}>
                        Enter Challenge <ChevronRight className="w-4 h-4" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Upcoming Challenges Section */}
        {!loading && upcomingChallenges.length > 0 && (
          <div className="mt-12" data-testid="upcoming-challenges-section">
            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-500" />
              Upcoming Challenges
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {upcomingChallenges.map(ch => (
                <Card key={ch.id} className="rounded-2xl shadow-sm border border-dashed border-blue-200 bg-gradient-to-br from-white to-blue-50/50 hover:shadow-md transition-all" data-testid={`upcoming-card-${ch.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shrink-0 opacity-80">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                        Coming Soon
                      </Badge>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{ch.title}</h3>
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">{ch.description || "A new quiz challenge is being prepared. Stay tuned!"}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                      {ch.subject_count > 0 && <span><BookOpen className="w-3.5 h-3.5 inline mr-1" />{ch.subject_count} subjects</span>}
                      <span><Calendar className="w-3.5 h-3.5 inline mr-1" />Season {ch.season_number}</span>
                    </div>
                    {ch.start_date && (
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                        <p className="text-xs font-semibold text-blue-700 text-center mb-3">Releases On</p>
                        <CountdownTimer targetDate={ch.start_date} />
                        <p className="text-xs text-blue-500 text-center mt-2 font-medium">
                          {new Date(ch.start_date).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
